// dlms-frontend/src/components/commons/ExpectedPaymentsPDF.tsx
import React from 'react';
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
} from '@react-pdf/renderer';

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 9,
    fontFamily: 'Helvetica',
  },
  header: {
    marginBottom: 12,
  },
  title: {
    fontSize: 13,
    fontFamily: 'Helvetica-Bold',
    marginBottom: 2,
  },
  subtitle: {
    fontSize: 9,
    color: '#555',
    marginBottom: 2,
  },
  summaryRow: {
    flexDirection: 'row',
    gap: 20,
    marginBottom: 10,
    marginTop: 4,
  },
  summaryBox: {
    borderWidth: 1,
    borderColor: '#ccc',
    padding: '4 8',
    minWidth: 80,
  },
  summaryLabel: {
    fontSize: 7,
    color: '#888',
    textTransform: 'uppercase',
  },
  summaryValue: {
    fontSize: 11,
    fontFamily: 'Helvetica-Bold',
  },
  table: {
    width: '100%',
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#1d3557',
    color: '#fff',
    padding: '4 3',
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#e8e8e8',
    padding: '4 3',
  },
  tableRowAlt: {
    backgroundColor: '#f9f9f9',
  },
  th: {
    fontFamily: 'Helvetica-Bold',
    color: '#fff',
    fontSize: 8,
  },
  td: {
    fontSize: 8,
    color: '#333',
  },
  // column widths — total must equal 100%
  colNo:     { width: '3%' },
  colName:   { width: '17%' },
  colPhone:  { width: '10%' },
  colBranch: { width: '13%' },
  colAmt:    { width: '11%'},
  colPaid:   { width: '13%'},
  colBal:    { width: '13%' },
  colLast:   { width: '12%' },
  colStatus: { width: '7%', textAlign: 'center' },
  badgePaid: {
    backgroundColor: '#d4edda',
    color: '#155724',
    padding: '1 4',
    fontSize: 7,
  },
  badgeNotPaid: {
    backgroundColor: '#f8d7da',
    color: '#721c24',
    padding: '1 4',
    fontSize: 7,
  },
  footer: {
    marginTop: 16,
    fontSize: 7,
    color: '#aaa',
    textAlign: 'right',
  },
});

export interface ExpectedPaymentRecord {
  clientName: string;
  clientPhone: string;
  branch: string;
  amountBorrowed: number;
  amountPaid: number;
  balance: number;
  lastPaymentDate: string | null;
  paymentStatus: 'PAID' | 'NOT PAID';
}

interface Props {
  data: ExpectedPaymentRecord[];
  date: string;
  branch: string;
  installmentPeriod: string;
  paid: number;
  notPaid: number;
}

const fmt = (n: number) =>
  n?.toLocaleString('en-UG', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) ?? '—';

const fmtDate = (d: string | null) =>
  d ? new Date(d).toLocaleDateString('en-GB') : '—';

const ExpectedPaymentsPDF: React.FC<Props> = ({
  data,
  date,
  branch,
  installmentPeriod,
  paid,
  notPaid,
}) => (
  <Document>
    <Page size="A4" orientation="landscape" style={styles.page}>
      {/* Header */}
      <View style={styles.header}>
        <Text style={styles.title}>Expected Payments Report</Text>
        <Text style={styles.subtitle}>
          Date: {new Date(date).toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          {branch ? `  |  Branch: ${branch}` : '  |  All Branches'}
          {installmentPeriod ? `  |  Period: ${installmentPeriod}` : '  |  All Periods'}
        </Text>
        <Text style={styles.subtitle}>
          Printed: {new Date().toLocaleString('en-GB')}
        </Text>
      </View>

      {/* Summary */}
      <View style={styles.summaryRow}>
        <View style={styles.summaryBox}>
          <Text style={styles.summaryLabel}>Total Expected</Text>
          <Text style={styles.summaryValue}>{data.length}</Text>
        </View>
        <View style={[styles.summaryBox, { borderColor: '#155724' }]}>
          <Text style={[styles.summaryLabel, { color: '#155724' }]}>Paid</Text>
          <Text style={[styles.summaryValue, { color: '#155724' }]}>{paid}</Text>
        </View>
        <View style={[styles.summaryBox, { borderColor: '#721c24' }]}>
          <Text style={[styles.summaryLabel, { color: '#721c24' }]}>Not Paid</Text>
          <Text style={[styles.summaryValue, { color: '#721c24' }]}>{notPaid}</Text>
        </View>
      </View>

      {/* Table */}
      <View style={styles.table}>
        {/* Table header */}
        <View style={styles.tableHeader}>
          <Text style={[styles.th, styles.colNo]}>#</Text>
          <Text style={[styles.th, styles.colName]}>Client Name</Text>
          <Text style={[styles.th, styles.colPhone]}>Phone</Text>
          <Text style={[styles.th, styles.colBranch]}>Branch</Text>
          <Text style={[styles.th, styles.colAmt]}>Borrowed</Text>
          <Text style={[styles.th, styles.colPaid]}>Paid</Text>
          <Text style={[styles.th, styles.colBal]}>Balance</Text>
          <Text style={[styles.th, styles.colLast]}>Last Payment</Text>
          <Text style={[styles.th, styles.colStatus]}>Status</Text>
        </View>

        {[...data].sort((a, b) => {
        if (a.paymentStatus === b.paymentStatus) return a.clientName.localeCompare(b.clientName);
        return a.paymentStatus === 'PAID' ? -1 : 1;
      }).map((row, i) => (
          <View
            key={i}
            style={[styles.tableRow, i % 2 === 1 ? styles.tableRowAlt : {}]}
          >
            <Text style={[styles.td, styles.colNo]}>{i + 1}</Text>
            <Text style={[styles.td, styles.colName]}>{row.clientName}</Text>
            <Text style={[styles.td, styles.colPhone]}>{row.clientPhone}</Text>
            <Text style={[styles.td, styles.colBranch]}>{row.branch || '—'}</Text>
            <Text style={[styles.td, styles.colAmt]}>{fmt(row.amountBorrowed)}</Text>
            <Text style={[styles.td, styles.colPaid]}>{fmt(row.amountPaid)}</Text>
            <Text style={[styles.td, styles.colBal]}>{fmt(row.balance)}</Text>
            <Text style={[styles.td, styles.colLast]}>{fmtDate(row.lastPaymentDate)}</Text>
            <Text
              style={[
                styles.td,
                styles.colStatus,
                row.paymentStatus === 'PAID' ? styles.badgePaid : styles.badgeNotPaid,
              ]}
            >
              {row.paymentStatus}
            </Text>
          </View>
        ))}
      </View>

      <Text style={styles.footer}>DLMS — Digital Loan Management System</Text>
    </Page>
  </Document>
);

export default ExpectedPaymentsPDF;
